import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';

const Container = styled.div`
  min-height: 100vh;
  background: var(--white);
  padding: 2rem;
`;

const Header = styled.header`
  background: var(--navy-blue);
  color: var(--white);
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  text-align: center;
  
  h1 {
    margin: 0 0 0.5rem 0;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
  }
`;

const QRGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const QRCard = styled.div`
  background: var(--white);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  
  h3 {
    margin: 0 0 1rem 0;
    color: var(--navy-blue);
    font-size: 1.5rem;
  }
`;

const QRPlaceholder = styled.div`
  width: 200px;
  height: 200px;
  background: var(--navy-blue);
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  font-size: 1.2rem;
  font-weight: bold;
  border-radius: 8px;
`;

const QRInfo = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-top: 1rem;
  
  code {
    display: block;
    background: #f0f0f0;
    padding: 0.5rem;
    margin-top: 0.5rem;
    border-radius: 4px;
    font-size: 0.8rem;
    word-break: break-all;
  }
`;

const PrintButton = styled.button`
  background: var(--primary-turquoise);
  color: var(--white);
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2db3a3;
  }
`;

const ControlPanel = styled.div`
  background: var(--white);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  max-width: 800px;
  margin: 0 auto 2rem;
`;

const TableInputs = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  
  label {
    font-weight: 600;
    color: var(--navy-blue);
  }
  
  input {
    padding: 0.5rem 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    width: 80px;
    text-align: center;
    
    &:focus {
      outline: none;
      border-color: var(--primary-turquoise);
    }
  }
`;

const GenerateButton = styled.button`
  background: var(--navy-blue);
  color: var(--white);
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #001F3F;
  }
`;

const QRGenerator: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const [tableCount, setTableCount] = useState(10);
  const [tables, setTables] = useState<number[]>([]);

  const generateTables = () => {
    const newTables = Array.from({ length: tableCount }, (_, i) => i + 1);
    setTables(newTables);
  };

  const getTableUrl = (tableNumber: number) => {
    return `${window.location.origin}/order/${locationId || 'cayma'}/table/${tableNumber}`;
  };

  const printQR = (tableNumber: number) => {
    // En una implementación real, esto generaría un PDF o abriría la ventana de impresión
    window.print();
  };

  const printAllQRs = () => {
    // En una implementación real, esto generaría un PDF con todos los QRs
    window.print();
  };

  return (
    <Container>
      <Header>
        <h1>Generador de Códigos QR</h1>
        <p>Pepe Tiburón - {locationId || 'Cayma'}</p>
      </Header>

      <ControlPanel>
        <TableInputs>
          <label>Número de mesas:</label>
          <input
            type="number"
            value={tableCount}
            onChange={(e) => setTableCount(parseInt(e.target.value) || 1)}
            min="1"
            max="50"
          />
          <GenerateButton onClick={generateTables}>
            Generar QRs
          </GenerateButton>
        </TableInputs>
        
        {tables.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <PrintButton onClick={printAllQRs}>
              <i className="fas fa-print"></i> Imprimir Todos los QRs
            </PrintButton>
          </div>
        )}
      </ControlPanel>

      <QRGrid>
        {tables.map(tableNum => (
          <QRCard key={tableNum}>
            <h3>Mesa {tableNum.toString().padStart(2, '0')}</h3>
            
            <QRPlaceholder>
              QR Code
              <br />
              Mesa {tableNum}
            </QRPlaceholder>
            
            <QRInfo>
              <p>Escanea para ver el menú y hacer tu pedido</p>
              <code>{getTableUrl(tableNum)}</code>
            </QRInfo>
            
            <PrintButton onClick={() => printQR(tableNum)}>
              <i className="fas fa-print"></i> Imprimir
            </PrintButton>
          </QRCard>
        ))}
      </QRGrid>
    </Container>
  );
};

export default QRGenerator;